/** Compact product card for the instant search dropdown grid. */

import { h } from 'preact';
import { useState, useRef } from 'preact/hooks';
import { getDisplayPriceParts, getProductUrl, trackClick, prefetchProductPage } from '../api';
import type { ProductResult } from '../api';
import { useConditionalTitle } from '../hooks/useConditionalTitle';
import { QuickTooltip, useQuickTooltip } from './QuickTooltip';

const TERRA_VENDOR = 'Terra Universal, Inc.';
const PREFETCH_HOVER_MS = 150;

interface InstantResultCardProps {
  product: ProductResult;
  query: string;
  position: number;
}

export function InstantResultCard({ product, query, position }: InstantResultCardProps) {
  const [imgError, setImgError] = useState(false);
  const productUrl = getProductUrl(product);
  const priceParts = getDisplayPriceParts(product);
  const hoverTimerRef = useRef<number | null>(null);
  const nameRef = useRef<HTMLSpanElement>(null);
  const nameTooltip = useConditionalTitle(nameRef, product.name);
  const { tooltip, showTooltip, moveTooltip, hideTooltip } = useQuickTooltip(nameTooltip);

  const handleMouseEnter = () => {
    if (productUrl) {
      hoverTimerRef.current = window.setTimeout(() => prefetchProductPage(productUrl), PREFETCH_HOVER_MS);
    }
  };

  const handleMouseLeave = () => {
    if (hoverTimerRef.current) { clearTimeout(hoverTimerRef.current); hoverTimerRef.current = null; }
    hideTooltip();
  };

  const handleClick = () => {
    trackClick(query, product.product_id, position);
    window.dataLayer?.push({
      event: 'select_item',
      ecommerce: {
        items: [
          {
            item_name: product.name,
            item_id: product.part_number,
            price: product.price,
            item_category: product.category,
            currency: 'USD',
            index: position,
            item_list_name: 'instant_search',
          },
        ],
      },
    });
  };

  return (
    <a
      href={productUrl}
      class="ts-instant-card"
      onClick={handleClick}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={(e) => {
        handleMouseEnter();
        showTooltip(e);
      }}
      onMouseMove={(e) => moveTooltip(e)}
      onMouseLeave={handleMouseLeave}
      onFocus={(e) => {
        handleMouseEnter();
        showTooltip(e);
      }}
      onBlur={handleMouseLeave}
      aria-label={product.name}
    >
      <div class="ts-instant-card-img-wrap">
        {product.image_url && !imgError ? (
          <img
            src={product.image_url}
            alt={product.name}
            class="ts-instant-card-img"
            width={80}
            height={80}
            loading={position < 4 ? 'eager' : 'lazy'}
            decoding="async"
            fetchpriority={position < 4 ? 'high' : 'low'}
            onError={() => setImgError(true)}
          />
        ) : (
          <div class="ts-instant-card-img ts-instant-card-img--placeholder">
            <span class="ts-result-card-image-initials" style="font-size:18px">
              {product.name
                .split(/[\s,]+/)
                .slice(0, 2)
                .map((w) => (w[0] || '').toUpperCase())
                .join('')}
            </span>
          </div>
        )}
      </div>
      <div class="ts-instant-card-info">
        <span ref={nameRef} class="ts-instant-card-name">{product.name}</span>
        <div class="ts-instant-card-meta">
          <span class="ts-instant-card-part">#{product.part_number}</span>
          <span class={`ts-instant-card-vendor ${product.vendor === TERRA_VENDOR ? 'ts-instant-card-vendor--terra' : 'ts-instant-card-vendor--le'}`}>
            {product.vendor === TERRA_VENDOR ? 'Terra' : 'Lab Equip'}
          </span>
        </div>
        {product.stocked && (
          <span class="ts-instant-card-stock">
            <span class="ts-instant-card-stock-dot" />
            In Stock
          </span>
        )}
        {priceParts && (
          <span class={`ts-instant-card-price ${priceParts.isQuote ? 'ts-instant-card-price--quote' : ''}`}>
            {priceParts.isQuote ? (
              priceParts.amount
            ) : (
              <span class="ts-price-text">
                {priceParts.prefix && <span class="ts-price-prefix">{priceParts.prefix}</span>}
                <span class="ts-price-amount">{priceParts.amount}</span>
              </span>
            )}
          </span>
        )}
      </div>
      <QuickTooltip tooltip={tooltip} />
    </a>
  );
}

export function InstantSkeletonCard() {
  return (
    <div class="ts-instant-card ts-instant-card--skeleton">
      <div class="ts-instant-card-img-wrap">
        <div class="ts-skeleton ts-instant-skeleton-img" />
      </div>
      <div class="ts-instant-card-info">
        <div class="ts-skeleton ts-skeleton--text ts-skeleton--w80" />
        <div class="ts-skeleton ts-skeleton--text ts-skeleton--w40" />
        <div class="ts-skeleton ts-skeleton--text ts-skeleton--w30" />
      </div>
    </div>
  );
}
